"use client";
import { useState, useEffect } from "react";
import { Spin, Empty } from "antd";
import { IoChevronDown } from "react-icons/io5";
import CourseService from "@/services/api/CourseService";
import HomePageService from "@/services/api/HomeService";
import CommonService from "@/services/api/CommonService";
import CourseCard from "@/components/common/card/CourseCard";
import SectionTitle from "@/components/common/section-title/SectionTitle";
import CommonImage from "@/components/common/image/CommonImage";
import ProgramCard from "@/components/common/card/ProgramCard";
import { useParams, useRouter } from "next/navigation";
import CoursePageCard from "./components/CoursePageCard";
import { useSelector } from "react-redux";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";

export default function CoursePage() {
  const params = useParams();
  const router = useRouter();
  const { groupSlug } = params;
  const setting = useSelector((state) => state.setting.data);
  const [activeGroupKey, setActiveGroupKey] = useState(groupSlug);

  const {
    data: groupsData,
    isLoading: groupsLoading,
    error: groupsError,
  } = useQuery({
    queryKey: ["course-groups"],
    queryFn: async () => {
      try {
        const res = await HomePageService.getAllGroup();

        const allCoursesGroup = {
          slug: "all",
          title: "সকল কোর্স",
          image: "/images/admission-icon/course-item-1.png",
        };

        const freeCourseGroup = {
          slug: "free",
          title: "ফ্রি কোর্স",
          image: "/images/admission-icon/course-item-5.png",
        };

        const apiGroups =
          res?.map((g) => ({
            slug: g.slug,
            title: g.name,
            image: g.icon_url ?? "/images/admission-icon/course-item-2.png",
          })) || [];

        return [allCoursesGroup, ...apiGroups, freeCourseGroup];
      } catch (error) {
        console.error("Error fetching groups", error);

        return [
          {
            slug: "all",
            title: "সকল কোর্স",
            image: "/images/admission-icon/course-item-1.png",
          },
          {
            slug: "free",
            title: "ফ্রি কোর্স",
            image: "/images/admission-icon/course-item-5.png",
          },
        ];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: coursesData,
    isLoading: coursesLoading,
    error: coursesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["courses", activeGroupKey],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        let res;

        if (activeGroupKey === "all") {
          res = await HomePageService.getGroupCourses(activeGroupKey, {
            type: "",
            page: pageParam,
          });
        } else if (activeGroupKey === "free") {
          res = await HomePageService.getGroupCourses("free", {
            type: "",
            page: pageParam,
          });
          if (res?.courses?.data) {
            res.courses.data = res.courses.data || [];
          }
        } else {
          res = await HomePageService.getGroupCourses(activeGroupKey, {
            type: "",
            page: pageParam,
          });
        }

        return {
          courses: res.courses?.data || [],
          nextPage: res.courses?.next_page_url ? pageParam + 1 : undefined,
          group:
            res.group ||
            (activeGroupKey === "all"
              ? { name: "সকল কোর্স", slug: "all" }
              : activeGroupKey === "free"
              ? { name: "ফ্রি কোর্স", slug: "free" }
              : null),
        };
      } catch (error) {
        console.error("Error fetching courses", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const courses = coursesData?.pages.flatMap((page) => page.courses) || [];
  const selectedGroup =
    coursesData?.pages[0]?.group ||
    (activeGroupKey === "all"
      ? { name: "সকল কোর্স", slug: "all" }
      : activeGroupKey === "free"
      ? { name: "ফ্রি কোর্স", slug: "free" }
      : null);

  const handleGroupClick = (group) => {
    if (!group?.slug) return;

    setActiveGroupKey(group.slug);
    router.push(`/courses/${group.slug}`, { shallow: true });
  };

  const handleLoadMore = () => {
    fetchNextPage();
  };

  useEffect(() => {
    if (groupsData && groupSlug) {
      const foundGroup =
        groupsData.find((g) => g.slug === groupSlug) || groupsData[0];
      if (foundGroup) {
        setActiveGroupKey(foundGroup.slug);
      }
    }
  }, [groupSlug, groupsData]);

  const loading = groupsLoading || coursesLoading;

  return (
    <CoursePageCard
      loading={loading}
      groups={groupsData || []}
      selectedGroup={selectedGroup}
      handleGroupClick={handleGroupClick}
      courses={courses}
      hasMore={hasNextPage}
      handleLoadMore={handleLoadMore}
      activeGroupKey={activeGroupKey}
      setting={setting}
      isFetchingNextPage={isFetchingNextPage}
    />
  );
}
